// src/components/MenuView.js

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../../../shared/components/layout/Layout';
import MenuDisplay from '../components/MenuDisplay';
import ShoppingList from '../../shopping-list/pages/ShoppingListPage';
import { Alert } from '../../../shared/components/ui';
import { menuService } from '../services/menuService';
import { shoppingListService } from '../../shopping-list/services/shoppingListService';
import { feedbackService } from '../../meal/services/feedbackService';
import { useTranslation } from 'react-i18next';

const MenuView = () => {
    const { date } = useParams();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [menu, setMenu] = useState(null);
    const [shoppingList, setShoppingList] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    const [isGeneratingList, setIsGeneratingList] = useState(false);
    const [menuMetadata, setMenuMetadata] = useState(null);

    // Cargar el menú específico por fecha
    useEffect(() => {
        loadMenuByDate();
    }, [date]);

    const loadMenuByDate = async () => {
        if (!date) {
            setError(t('menu.view.invalidDate'));
            setIsLoading(false);
            return;
        }

        setIsLoading(true);
        setError('');

        try {
            // Usar el nuevo endpoint específico para obtener el menú por fecha
            const response = await menuService.getMenuByDate(date);

            if (response.menu) {
                setMenu(response.menu);
                setMenuMetadata(response.menu_metadata || null);
            } else {
                setError(t('menu.errors.notFound'));
            }
        } catch (err) {
            // Si es un error 404, usar el mensaje del error
            if (err.message.includes('no encontrado') || err.message.includes('not found')) {
                setError(t('menu.errors.notFound'));
            } else {
                setError(t('menu.errors.loadFailed'));
            }
        } finally {
            setIsLoading(false);
        }
    };

    // Maneja la confirmación del menú y generación de lista de compra
    const handleMenuConfirmation = async () => {
        if (!menu) return;

        setIsGeneratingList(true);
        setError('');
        try {
            const list = await shoppingListService.generateShoppingList(menu);
            setShoppingList(list);
        } catch (err) {
            setError(t('shoppingList.errors.loadGeneral'));
        } finally {
            setIsGeneratingList(false);
        }
    };

    // Maneja el reordenamiento de meals
    const handleMealReorder = async (typeMapping) => {
        try {
            // Llamar a la API para actualizar los tipos
            await menuService.reorderMeals(date, typeMapping);

            // No necesitamos recargar el menú completo, el estado local ya está actualizado
            // El backend ya procesó el cambio correctamente
        } catch (error) {
            throw error; // Re-lanzar el error para que MenuDisplay lo maneje
        }
    };

    // Formatear la fecha para mostrar
    const formatDate = (dateString) => {
        if (!dateString) return t('shoppingList.notAvailable');
        const date = new Date(dateString);
        return date.toLocaleDateString('es-ES', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    const getDayName = (dateString) => {
        if (!dateString) return t('plan.days.day') || 'Día';
        const date = new Date(dateString);
        const dayNames = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
        return dayNames[date.getDay()];
    };

    if (isLoading) {
        return (
            <Layout title={t('menu.view.loadingTitle') || t('common.loading')}>
                <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600 mx-auto"></div>
                    <p className="mt-4 text-gray-600">{t('menu.view.loading') || t('common.loading')}</p>
                </div>
            </Layout>
        );
    }

    if (error) {
        return (
            <Layout title={t('common.error')}>
                <div className="max-w-2xl mx-auto">
                    <Alert variant="error">
                        <p>{error}</p>
                    </Alert>
                    <div className="mt-4 text-center">
                        <button
                            onClick={() => navigate('/plan')}
                            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                        >
                            {t('plan.page.title') || t('navigation.plan')}
                        </button>
                    </div>
                </div>
            </Layout>
        );
    }

    if (!menu) {
        return (
            <Layout title={t('menu.errors.notFound')}>
                <div className="max-w-2xl mx-auto">
                    <Alert variant="warning">
                        <p>{t('menu.errors.notFound')}</p>
                    </Alert>
                    <div className="mt-4 text-center">
                        <button
                            onClick={() => navigate('/plan')}
                            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                        >
                            {t('plan.page.title') || t('navigation.plan')}
                        </button>
                    </div>
                </div>
            </Layout>
        );
    }

    return (
        <Layout title={`${t('navigation.menu')} - ${getDayName(date)}`}>
            {/* Header con información de la fecha */}
            <div className="max-w-4xl mx-auto mb-6">
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                    <div className="flex items-center justify-between">
                        <div>
                            <h1 className="text-2xl font-bold text-gray-900">
                                {getDayName(date)}
                            </h1>
                            <p className="text-gray-600 mt-1">
                                {formatDate(date)}
                            </p>
                        </div>
                        <button
                            onClick={() => navigate('/plan')}
                            className="px-4 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
                        >
                            ← {t('plan.page.title') || t('navigation.plan')}
                        </button>
                    </div>
                </div>
            </div>

            {/* Mostrar estado de carga para la lista de compra */}
            {isGeneratingList && (
                <Alert variant="info" className="max-w-2xl mx-auto">
                    <p>{t('menu.view.generatingList') || t('shoppingList.loading')}</p>
                </Alert>
            )}

            {/* Mostrar el menú con funcionalidad de reordenamiento */}
            <MenuDisplay
                menu={menu}
                onConfirm={handleMenuConfirmation}
                canGiveFeedback={true}
                feedbackService={feedbackService}
                canReorder={true}
                onMealReorder={handleMealReorder}
                menuMetadata={menuMetadata}
            />

            {/* Mostrar la lista de compra si está disponible */}
            {shoppingList && <ShoppingList list={shoppingList} />}
        </Layout>
    );
};

export default MenuView;
